/* eslint-disable import/first */

import React from 'react';
import { View, Pressable } from 'react-native';
import classNames from 'classnames';

import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import { BodyText } from '../../ui';
import StateLink from '../../StateLink';
import HeaderMenu from './HeaderMenu';
import { Container as SidebarToggle } from '../../shared/SidebarToggle';
import Image from '../../shared/Image';
import Logo from '../../../../images/styleseat-logo-black.svg';
import { ReferralEntryTrigger } from './ReferralEntryTrigger';
import NavSearchBar from '../NavSearchBar';
import './Header.module.scss';
import { ShareTrigger } from './Header.utils';
import { HeaderProps } from './types';
import { HeaderSearchBar } from './HeaderSearchBar';
import { MobileSearch } from './MobileSearch';

/**
 * Header - wrapping component to display the client header to anonymous and client users.
 *
 * Usage:
 * ```jsx
 * <Header />
 * ```
 *
 * This component takes no props and is connected to the redux store.
 * @deprecated Use the Header component instead
 */
export const DeprecatedHeader: React.FCWithChildren<HeaderProps> = ({
  providerId,
  referralShareProps,
  headerSearchBarText,
  utmParams = {},
  breakpoints,

  showSidebarToggle = true,
  showSearch = true,
  showMobileSearch,
  showMobileSearchDot = false,
  showSetUpBusinessLink = false,
  showMobileBorder = true,
  showReferralEntry = false,
  showHeaderSearchBar = false,

  onClickSetUpMyBusiness,
  onMobileSearchClick,
}) => {
  const referralEntry = React.useMemo(() => (
    <ShareTrigger
      {...referralShareProps}
      renderTrigger={onClick => (
        <ReferralEntryTrigger
          onClick={onClick}
          appearance={breakpoints.mobile || breakpoints.tablet ? 'normal' : 'header'}
        />
      )}
    />
  ), [
    referralShareProps,
    breakpoints,
  ]);

  return (
    <View styleName="styleseat-header-component">
      <View
        testID="HeaderUI"
        styleName={classNames('styleseat-header', {
          noBorder: !showMobileBorder,
        })}
      >
        <View styleName="left-right-bar-wrapper">
          <View styleName="leftBar">
            {showSidebarToggle && (
              <View styleName="styleseat-header__toggleWrapper">
                <SidebarToggle />
              </View>
            )}
            <StateLink to="home" testID="header-logo" params={utmParams}>
              <Image
                source={Logo}
                styleName="styleseat-header__styleseat-logo"
              />
            </StateLink>
            <View />
            {showSearch && (
              <View styleName="search-bar-wrapper">
                <NavSearchBar />
              </View>
            )}
            {showSetUpBusinessLink && (
              (
                <Inline
                  alignItems="center"
                  paddingRight={20}
                >
                  <Pressable
                    onPress={onClickSetUpMyBusiness}
                    testID="set-up-my-business-link"
                  >
                    <Inline paddingRight={2}>
                      <BodyText
                        color={BrandColor.FloraDark}
                        appearance="normal"
                        textAlign="center"
                        weight="bold"
                      >
                        {'Set Up My\nBusiness'}
                      </BodyText>
                    </Inline>
                  </Pressable>
                </Inline>
              )
            )}
            {(breakpoints.mobile || breakpoints.tablet)
            && (showMobileSearch || showReferralEntry) && (
              <Inline paddingY={8} alignItems="center">
                {showReferralEntry && (
                  <Box paddingX={12}>
                    {referralEntry}
                  </Box>
                )}
                <MobileSearch
                  showMobileSearch={showMobileSearch && !showHeaderSearchBar}
                  showMobileSearchDot={showMobileSearchDot}
                  onMobileSearchClick={onMobileSearchClick}
                />
              </Inline>
            )}
          </View>
          <HeaderMenu
            providerId={providerId}
            referralEntry={referralEntry}
            showReferralEntry={showReferralEntry}
          />
        </View>
        <Box paddingX={16}>
          <HeaderSearchBar
            headerSearchBarText={headerSearchBarText}
            showHeaderSearchBar={showHeaderSearchBar}
          />
        </Box>
      </View>
    </View>
  );
};
